import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useUserContext } from "../../context";
import { Link } from "react-router-dom";
import { Subhai } from "..";

// Custom tooltip content component using default font
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const { name, per_chg } = payload[0].payload;
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "#000",
          border: "1px solid #ccc",
          padding: "5px",
          fontFamily: "inherit",
        }}
      >
        <p>{name}</p>
        <p>{per_chg}%</p>
      </div>
    );
  }
  return null;
};

// Custom label for placing stock names at the bottom using default font
const CustomVerticalLabel = ({ props }) => {
  const { x, y, width, value, height } = props;
  const labelX = x + width / 2;
  const labelY = height > 0 ? y + height - 10 : height + y + 10; // fixed position at bottom
  return (
    <text
      x={labelX}
      y={labelY}
      textAnchor="left"
      fill="#fff"
      fontSize={14}
      fontFamily="inherit"
      transform={`rotate(${height >= 0 ? -90 : 90}, ${labelX}, ${labelY})`}
    >
      {value}
    </text>
  );
};

// Custom bar shape to render the bar with rounded top corners
const renderCustomBar = (props) => {
  const { x, y, width, height, payload } = props;
  const isPositive = payload.isPositive;
  const barFill = isPositive ? "url(#greenGradient)" : "url(#redGradient)";
  const radius = 10;

  let d;

  if (isPositive) {
    // Top corners rounded (bar grows up)
    d = `
      M${x},${y + height}
      L${x},${y + radius}
      Q${x},${y} ${x + radius},${y}
      L${x + width - radius},${y}
      Q${x + width},${y} ${x + width},${y + radius}
      L${x + width},${y + height}
      Z
    `;
  } else {
    // Bottom corners rounded (bar grows down)
    d = `
       M${x},${y + height}
    L${x},${y}
    Q${x},${y + radius} ${x + radius},${y + radius}
    L${x + width - radius},${y + radius}
    Q${x + width},${y + radius} ${x + width},${y}
    L${x + width},${y + height}
    Z
    `;
  }

  return (
    <g style={{ pointerEvents: "all" }}>
      <path d={d} fill={barFill} />
    </g>
  );
};


const UpBarChart = ({ small = true }) => {
  const [data, setData] = useState([]);
  const { userdetails } = useUserContext();
  const chartHeight = 450; // fixed chart height
  const storedData = sessionStorage.getItem("index");

  useEffect(() => {
    setTimeout(() => {
      if (storedData && storedData !== "undefined") {
        // Include per_chg in the mapping for tooltip display.
        setData(
          JSON.parse(storedData)?.map((item) => ({
            name: item.name, // stock name
            per_chg: item.per_chg, // original percentage change
            isPositive: item.per_chg > 0,
            // showLabel: Math.abs(item.per_chg) >= 0, // adjust threshold as needed
            showLabel: true, // adjust threshold as needed
          })) || []
        );
      }
    }, 500);
  }, [storedData]);

  // Process top 4 and bottom 4 if data exists
  const processedData =
    data.length > 0
      ? (() => {
        const sortedData = [...data].sort((a, b) => b.per_chg - a.per_chg);
        const top4 = sortedData.slice(0, 4);
        const bottom4 = sortedData.slice(-4);
        return [...top4, ...bottom4];
      })()
      : [];

  return (
    <div className="px-4 py-4 bg-darkMode-layer1 rounded-xl shadow-lg flex flex-col items-center relative font-sans">
      {/* Header Section */}
      <div className="flex flex-row justify-between items-center w-full">
        <h2 className="text-lg font-medium text-white">Sector Analysis</h2>
        <Link to={"/Sector"}>
          <button className="group bg-darkMode-layer2 text-darkMode-text px-5 py-2 rounded-full shadow-md hover:ring-2 hover:ring-darkMode-green transition-all duration-300">
            <div className="-rotate-45 group-hover:rotate-0 transition-all duration-300">
              <FontAwesomeIcon icon={faArrowRight} />
            </div>
          </button>
        </Link>
      </div>

      <div className="mt-4 relative items-center justify-center flex w-full min-h-[250px] sm:min-h-[350px] md:min-h-[450px]">
        <Subhai />

        {/* Chart Section */}
        <ResponsiveContainer
          width={small ? "100%" : "80%"}
          height={chartHeight}
        >
          <BarChart
            data={processedData}
            margin={{
              top: 80,
              right: small ? 20 : 0,
              bottom: 50,
              left: small ? 20 : 60,
            }}
          >
            {/* Gradient definitions */}
            <defs>
              <linearGradient id="greenGradient" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#005930" /> {/* Dark green */}
                <stop offset="100%" stopColor="#01e47c" /> {/* Normal green */}
              </linearGradient>
              <linearGradient id="redGradient" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#7f3c3c" /> {/* Dark red */}
                <stop offset="100%" stopColor="#ff6061" /> {/* Normal red */}
              </linearGradient>
            </defs>
            <XAxis hide />
            <YAxis hide />
            {/* Horizontal axis line */}
            <ReferenceLine y={0} stroke="#333333" strokeWidth={2} />
            {/* Light grid behind the chart */}
            <CartesianGrid stroke="#222222" strokeDasharray="1 1" />
            {/* Tooltip with custom content */}
            <Tooltip content={<CustomTooltip />} cursor={false} />
            ``
            <Bar
              dataKey="per_chg"
              shape={renderCustomBar}
              isAnimationActive={true}
              animationBegin={200}
              animationDuration={300}
              animationEasing="ease-out"
            >
              <LabelList
                dataKey="name"
                content={(props) => {
                  const { index, x, width, value } = props;
                  if (!processedData[index].showLabel) return null;
                  return (
                    <CustomVerticalLabel
                      props={props}
                    />
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default UpBarChart;
